// ============================================================================
// AUDIT FIRM FORM MODAL — FSA Signature Workflow
// ============================================================================
// Create/Edit master form for fsa.tbl_audit_firm. Follows the standard
// Form Modal Blueprint (unsaved changes tracking, inline validation,
// 3-stage save flow with ConfirmDialog + loading + toast).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { fsaAuditFirmService } from 'api/services/fsa/fsaSignatureService';
import type { AuditFirm } from 'api/types/fsa/signature.types';

export interface AuditFirmFormData {
  firmName: string;
  firmRegistrationNumber: string;
  reportAttestationText: string;
  signatureBlockText: string;
  address: string;
  email: string;
  phone: string;
  website: string;
}

const INITIAL_FORM_DATA: AuditFirmFormData = {
  firmName: '',
  firmRegistrationNumber: '',
  reportAttestationText: 'As per our Report of even date attached',
  signatureBlockText: '',
  address: '',
  email: '',
  phone: '',
  website: '',
};

interface AuditFirmFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: AuditFirm | null;
  onSaved: () => void;
  onClose: () => void;
}

const AuditFirmFormModal: React.FC<AuditFirmFormModalProps> = ({ isOpen, mode, record, onSaved, onClose }) => {
  const [formData, setFormData] = useState<AuditFirmFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const recordRef = useRef(record);
  recordRef.current = record;
  const initialSnapshotRef = useRef('');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // Populate form on open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    const data: AuditFirmFormData = recordRef.current ? {
      firmName:               recordRef.current.firmName,
      firmRegistrationNumber: recordRef.current.firmRegistrationNumber,
      reportAttestationText:  recordRef.current.reportAttestationText ?? '',
      signatureBlockText:     recordRef.current.signatureBlockText ?? '',
      address:                recordRef.current.address ?? '',
      email:                  recordRef.current.email ?? '',
      phone:                  recordRef.current.phone ?? '',
      website:                recordRef.current.website ?? '',
    } : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.firmName.trim())               errors.firmName = 'Firm name is required';
    if (!formData.firmRegistrationNumber.trim()) errors.firmRegistrationNumber = 'FRN is required';
    if (formData.firmName.length > 200)          errors.firmName = 'Firm name cannot exceed 200 characters';
    if (formData.firmRegistrationNumber.length > 20) errors.firmRegistrationNumber = 'FRN cannot exceed 20 characters';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        firmName:               formData.firmName.trim(),
        firmRegistrationNumber: formData.firmRegistrationNumber.trim(),
        reportAttestationText:  formData.reportAttestationText.trim() || null,
        signatureBlockText:     formData.signatureBlockText.trim() || null,
        address:                formData.address.trim() || null,
        email:                  formData.email.trim() || null,
        phone:                  formData.phone.trim() || null,
        website:                formData.website.trim() || null,
        statusCode:             'ACTIVE',
      };
      if (mode === 'create') {
        await fsaAuditFirmService.create(payload);
      } else if (recordRef.current) {
        await fsaAuditFirmService.update(recordRef.current.id, {
          ...payload,
          rowVersion: recordRef.current.rowVersion,
        });
      }
      setIsSaving(false);
      notificationService.toast.success(`"${formData.firmName}" ${mode === 'create' ? 'created' : 'updated'} successfully.`);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const msg = err?.response?.data?.message ?? 'Save failed';
      notificationService.toast.error(msg, 6000);
    }
  }, [mode, formData, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'Add Audit Firm' : 'Edit Audit Firm'}</h2>
          <button className="modal-close" onClick={handleClose}><IconSystem.x size={20} /></button>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="firmName">
              <label className="form-label">Firm Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('firmName') ? ' form-field-error' : ''}`}
                value={formData.firmName}
                onChange={(e) => { clearFieldError('firmName'); setFormData(prev => ({ ...prev, firmName: e.target.value })); }}
                maxLength={200}
              />
              {getFieldError('firmName') && <div className="form-error-message">{getFieldError('firmName')}</div>}
            </div>
            <div className="form-group" data-field="firmRegistrationNumber">
              <label className="form-label">Firm Registration Number (FRN) <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('firmRegistrationNumber') ? ' form-field-error' : ''}`}
                value={formData.firmRegistrationNumber}
                onChange={(e) => { clearFieldError('firmRegistrationNumber'); setFormData(prev => ({ ...prev, firmRegistrationNumber: e.target.value })); }}
                placeholder="e.g. 101248W/W-100022"
                maxLength={20}
              />
              {getFieldError('firmRegistrationNumber') && <div className="form-error-message">{getFieldError('firmRegistrationNumber')}</div>}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Report Attestation Text</label>
            <input
              type="text"
              className="form-input"
              value={formData.reportAttestationText}
              onChange={(e) => setFormData(prev => ({ ...prev, reportAttestationText: e.target.value }))}
              maxLength={500}
              placeholder="As per our Report of even date attached"
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Auditor preamble line printed at the top of the signature block. Default ICAI wording shown.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Signature Block Text</label>
            <input
              type="text"
              className="form-input"
              value={formData.signatureBlockText}
              onChange={(e) => setFormData(prev => ({ ...prev, signatureBlockText: e.target.value }))}
              maxLength={500}
              placeholder={formData.firmName ? `For ${formData.firmName}` : 'For {firm name}'}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
              Firm-name heading printed at the top of the signature block. Empty falls back to "For {'{firm name}'}" — typical Indian FS convention.
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              rows={2}
            />
          </div>

          <div className="form-grid-3">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                maxLength={200}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                type="text"
                className="form-input"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                maxLength={50}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Website</label>
              <input
                type="text"
                className="form-input"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                maxLength={200}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`${mode === 'create' ? 'Create' : 'Update'} audit firm "${formData.firmName}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default AuditFirmFormModal;
